import React from 'react';
import axios from 'axios';
import moment from 'moment';
import {
    MDBBtn,
    MDBModal,
    MDBModalDialog,
    MDBModalContent,
    MDBModalHeader,
    MDBModalTitle,
    MDBModalBody,
} from 'mdb-react-ui-kit';
import { baseURL } from '../../api/baseURL';

/**
 * Read-only month calendar that visualises one photographer's availability.
 *
 * Data model (mirrors Backend's Photographer.availability sub-document):
 *   - `is_available`               whether the photographer has switched on availability at all
 *   - `available_from / _until`    inclusive window of accepting bookings
 *   - `custom_slots[{ date, slots[{from,to}] }]`
 *                                  per-date narrowing — when missing/empty for an
 *                                  in-window date, photographer is available all day
 *
 * Date cell colouring:
 *   - past dates                 → neutral gray (out of scope)
 *   - in availability window     → green (clickable)
 *   - everything else            → red (not clickable; nothing to show)
 *
 * The slot detail line is only rendered when a green (available) date is tapped.
 */

type Slot = { from: string; to: string };
type CustomSlot = { date: string; slots: Slot[] };
type Availability = {
    is_available?: boolean;
    available_from?: string | null;
    available_until?: string | null;
    custom_slots?: CustomSlot[];
};

type Props = {
    open: boolean;
    toggleOpen: () => void;
    photographer: any; // row item from photographer list
    token: string;
};

type State = {
    loading: boolean;
    err: string | null;
    availability: Availability | null;
    name: string | null;
    selectedDate: string | null; // YYYY-MM-DD
    // The month we're displaying. Start with the current month; users can
    // navigate prev/next within the photographer's availability window.
    viewYear: number;
    viewMonth: number; // 0-11
};

const TODAY_KEY = () => moment().format('YYYY-MM-DD');

// Slot labels shown to admins ─ the four 24h presets the Photographer app uses.
function describeSlot(s: Slot): string {
    if (!s || !s.from || !s.to) return '—';
    const fmt = (hhmm: string) => {
        const m = moment(hhmm, 'HH:mm');
        return m.isValid() ? m.format('h:mm A') : hhmm;
    };
    return `${fmt(s.from)} – ${fmt(s.to)}`;
}

// Mirrors the Photographer app's own semantics (AvailabilityScreen.tsx):
// a custom_slots entry for a date with an EMPTY slots array means the
// photographer explicitly marked that whole date unavailable — distinct
// from no entry at all (available all day). Previously unhandled here, so
// admin's calendar stayed green for a date the photographer had turned off.
function isExplicitlyUnavailable(dateStr: string, av: Availability | null): boolean {
    const entry = av?.custom_slots?.find((s) => s.date === dateStr);
    return !!entry && Array.isArray(entry.slots) && entry.slots.length === 0;
}

function inWindow(dateStr: string, av: Availability | null): boolean {
    if (!av?.is_available) return false;
    if (!av.available_from || !av.available_until) return false;
    if (isExplicitlyUnavailable(dateStr, av)) return false;
    const d = moment(dateStr, 'YYYY-MM-DD').startOf('day');
    const from = moment(av.available_from).startOf('day');
    const until = moment(av.available_until).startOf('day');
    if (!d.isValid() || !from.isValid() || !until.isValid()) return false;
    return d.isSameOrAfter(from) && d.isSameOrBefore(until);
}

function slotsForDate(dateStr: string, av: Availability | null): Slot[] | 'all_day' {
    const entry = av?.custom_slots?.find((s) => s.date === dateStr);
    if (!entry || !Array.isArray(entry.slots) || entry.slots.length === 0) {
        return 'all_day';
    }
    return entry.slots;
}

class PhotographerAvailabilityModal extends React.Component<Props, State> {
    state: State = {
        loading: false,
        err: null,
        availability: null,
        name: null,
        selectedDate: null,
        viewYear: moment().year(),
        viewMonth: moment().month(),
    };

    componentDidMount() {
        if (this.props.open && this.props.photographer?._id) {
            this.fetchAvailability();
        }
    }

    componentDidUpdate(prev: Props) {
        // Fetch when modal opens, or when a different photographer is opened
        // (the list re-uses one modal instance keyed by row click).
        const justOpened = !prev.open && this.props.open;
        const photographerChanged =
            this.props.photographer?._id && prev.photographer?._id !== this.props.photographer?._id;
        if ((justOpened || photographerChanged) && this.props.photographer?._id) {
            // Reset viewing month + selected date for a fresh photographer.
            this.setState({
                selectedDate: null,
                viewYear: moment().year(),
                viewMonth: moment().month(),
            });
            this.fetchAvailability();
        }
    }

    fetchAvailability = () => {
        const id = this.props.photographer?._id;
        if (!id || !this.props.token) return;
        this.setState({ loading: true, err: null, availability: null });
        axios
            .get(`${baseURL}/photographer-availability`, {
                params: { photographerId: id },
                headers: { Authorization: `Bearer ${this.props.token}` },
            })
            .then((resp: any) => {
                if (resp?.data?.message === 'success') {
                    const availability: Availability | null = resp.data.availability || null;
                    // Client-reported: the calendar always opened on today's month
                    // regardless of when this specific photographer's window
                    // actually starts, so it looked "different"/broken from one
                    // photographer to the next. Jump straight to the window's
                    // start month when there is one, so admin always lands on
                    // the actual relevant view.
                    const startMoment = availability?.is_available && availability?.available_from
                        ? moment(availability.available_from)
                        : null;
                    this.setState({
                        availability,
                        name: resp.data.name || null,
                        loading: false,
                        err: null,
                        ...(startMoment && startMoment.isValid()
                            ? { viewYear: startMoment.year(), viewMonth: startMoment.month() }
                            : {}),
                    });
                } else {
                    this.setState({ loading: false, err: 'Failed to load availability.' });
                }
            })
            .catch((e: any) => {
                const msg = e?.response?.data?.message || 'Failed to load availability.';
                this.setState({ loading: false, err: typeof msg === 'string' ? msg : 'Failed to load availability.' });
            });
    };

    onPickDate = (dateStr: string) => {
        // Only allow selecting in-window (green) dates — matches the user spec
        // that the slot detail should only show up when clicked on an available date.
        if (!inWindow(dateStr, this.state.availability)) return;
        this.setState({ selectedDate: dateStr });
    };

    onPrevMonth = () => {
        const m = moment({ year: this.state.viewYear, month: this.state.viewMonth }).subtract(1, 'month');
        this.setState({ viewYear: m.year(), viewMonth: m.month(), selectedDate: null });
    };

    onNextMonth = () => {
        const m = moment({ year: this.state.viewYear, month: this.state.viewMonth }).add(1, 'month');
        this.setState({ viewYear: m.year(), viewMonth: m.month(), selectedDate: null });
    };

    renderCalendarGrid() {
        const av = this.state.availability;
        const first = moment({ year: this.state.viewYear, month: this.state.viewMonth, day: 1 });
        const daysInMonth = first.daysInMonth();
        const startWeekday = first.day(); // 0 (Sun) - 6 (Sat)
        const todayKey = TODAY_KEY();

        // Pad: empty cells at start so the 1st aligns to its weekday column.
        const cells: Array<{ key: string; dateStr?: string; day?: number; isPast: boolean; isToday: boolean; isAvailable: boolean }> = [];
        for (let i = 0; i < startWeekday; i++) {
            cells.push({ key: `pad-${i}`, isPast: false, isToday: false, isAvailable: false });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            const dateStr = moment({ year: this.state.viewYear, month: this.state.viewMonth, day: d }).format('YYYY-MM-DD');
            const isPast = moment(dateStr).isBefore(moment().startOf('day'));
            const isToday = dateStr === todayKey;
            const isAvailable = !isPast && inWindow(dateStr, av);
            cells.push({ key: dateStr, dateStr, day: d, isPast, isToday, isAvailable });
        }

        // Group into rows of 7 for layout.
        const rows: Array<typeof cells> = [];
        for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));

        const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        return (
            <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 8 }}>
                    {weekdayLabels.map((w) => (
                        <div key={w} style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.55)', letterSpacing: 0.4, textTransform: 'uppercase' }}>
                            {w}
                        </div>
                    ))}
                </div>
                {rows.map((row, ri) => (
                    <div key={`row-${ri}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 6 }}>
                        {row.map((cell) => this.renderCell(cell))}
                    </div>
                ))}
            </div>
        );
    }

    renderCell(cell: { key: string; dateStr?: string; day?: number; isPast: boolean; isToday: boolean; isAvailable: boolean }) {
        if (!cell.dateStr) {
            return <div key={cell.key} style={{ height: 44 }} />;
        }
        const isSelected = this.state.selectedDate === cell.dateStr;
        // Color rules: past = neutral; in-window = green; else (no-availability or out of window) = red.
        // Background is the colour, but the day number stays clearly visible.
        let background = 'rgba(220, 53, 69, 0.28)';     // red default (unavailable)
        let borderColor = 'rgba(220, 53, 69, 0.5)';
        let cursor: 'default' | 'pointer' = 'default';
        if (cell.isPast) {
            background = 'rgba(255,255,255,0.05)';
            borderColor = 'rgba(255,255,255,0.12)';
        } else if (cell.isAvailable) {
            background = 'rgba(40, 167, 69, 0.32)';
            borderColor = 'rgba(40, 167, 69, 0.55)';
            cursor = 'pointer';
        }
        if (isSelected) {
            borderColor = '#29B6F7';
        }
        return (
            <div
                key={cell.key}
                onClick={() => cell.isAvailable && cell.dateStr ? this.onPickDate(cell.dateStr) : undefined}
                role={cell.isAvailable ? 'button' : undefined}
                aria-label={cell.dateStr}
                style={{
                    height: 44,
                    borderRadius: 8,
                    background,
                    border: `1px solid ${borderColor}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#FFFFFF',
                    fontWeight: cell.isToday ? 700 : 500,
                    cursor,
                    userSelect: 'none',
                    opacity: cell.isPast ? 0.55 : 1,
                    boxShadow: isSelected ? '0 0 0 2px rgba(41,182,247,0.35)' : 'none',
                    transition: 'box-shadow 120ms ease',
                }}
            >
                <span style={{ fontSize: 14, lineHeight: 1 }}>{cell.day}</span>
                {cell.isToday ? <span style={{ fontSize: 9, opacity: 0.8, marginTop: 2 }}>TODAY</span> : null}
            </div>
        );
    }

    renderSlotDetails() {
        const dateStr = this.state.selectedDate;
        if (!dateStr) return null;
        const slots = slotsForDate(dateStr, this.state.availability);
        const human = moment(dateStr).format('dddd, D MMMM YYYY');

        const wrap: React.CSSProperties = {
            marginTop: 18,
            padding: '14px 16px',
            background: 'rgba(40, 167, 69, 0.12)',
            border: '1px solid rgba(40, 167, 69, 0.35)',
            borderRadius: 10,
        };
        if (slots === 'all_day') {
            return (
                <div style={wrap}>
                    <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 4 }}>{human}</div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>Photographer is available all day.</div>
                </div>
            );
        }
        return (
            <div style={wrap}>
                <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 6 }}>{human}</div>
                <div style={{ fontSize: 14, marginBottom: 8 }}>On this date, photographer is available in these time slots:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {slots.map((s, i) => (
                        <span key={`${s.from}-${s.to}-${i}`} style={{
                            padding: '6px 10px',
                            borderRadius: 999,
                            background: 'rgba(40, 167, 69, 0.28)',
                            border: '1px solid rgba(40, 167, 69, 0.55)',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#FFFFFF',
                        }}>
                            {describeSlot(s)}
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    renderHeaderBar() {
        const monthLabel = moment({ year: this.state.viewYear, month: this.state.viewMonth }).format('MMMM YYYY');
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <MDBBtn size='sm' color='secondary' onClick={this.onPrevMonth}>‹ Prev</MDBBtn>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{monthLabel}</div>
                <MDBBtn size='sm' color='secondary' onClick={this.onNextMonth}>Next ›</MDBBtn>
            </div>
        );
    }

    render() {
        const { open, toggleOpen, photographer } = this.props;
        const av = this.state.availability;
        const photographerName = this.state.name || photographer?.name || 'Photographer';

        // Snapshot status helpers for the top info strip.
        const isOn = !!av?.is_available;
        const windowText = isOn && av?.available_from && av?.available_until
            ? `${moment(av.available_from).format('D MMM')} – ${moment(av.available_until).format('D MMM YYYY')}`
            : 'No active availability window';

        return (
            <MDBModal open={open} onClose={toggleOpen} tabIndex='-1'>
                {/* Width tuned: 800 → 700 → 490 → 420 across requests. Dropped size="lg"
                    here so MDB's own 800px max-width rule can't override our cap. At 420px
                    the 7-column calendar gives each cell ~50px which still fits the day
                    number + "TODAY" pill. `width: '100%'` keeps mobile responsive. */}
                <MDBModalDialog style={{ maxWidth: 420, width: '100%' }}>
                    <MDBModalContent style={{ backgroundColor: '#191c24', color: '#FFFFFF' }}>
                        <MDBModalHeader style={{ borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                            <div style={{ flex: 1 }}>
                                <MDBModalTitle>
                                    <span>Availability — {photographerName}</span>
                                </MDBModalTitle>
                                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                                    For detail timing slots, click on any available date from the calendar to see it.
                                </div>
                            </div>
                            <MDBBtn
                                floating
                                color='light'
                                onClick={toggleOpen}
                                aria-label='Close'
                                style={{ display: 'flex', padding: 5, height: 30, width: 30, background: 'rgba(255,255,255,0.1)' }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 30 30" fill="#FFFFFF">
                                    <path d="M 7 4 C 6.744 4 6.488 4.097 6.293 4.293 L 4.293 6.293 C 3.902 6.684 3.902 7.317 4.293 7.707 L 11.586 15 L 4.293 22.293 C 3.902 22.684 3.902 23.317 4.293 23.707 L 6.293 25.707 C 6.684 26.098 7.317 26.098 7.707 25.707 L 15 18.414 L 22.293 25.707 C 22.683 26.098 23.317 26.098 23.707 25.707 L 25.707 23.707 C 26.098 23.316 26.098 22.683 25.707 22.293 L 18.414 15 L 25.707 7.707 C 26.098 7.317 26.098 6.683 25.707 6.293 L 23.707 4.293 C 23.316 3.902 22.683 3.902 22.293 4.293 L 15 11.586 L 7.707 4.293 C 7.512 4.097 7.256 4 7 4 z" />
                                </svg>
                            </MDBBtn>
                        </MDBModalHeader>
                        <MDBModalBody>
                            {/* Status strip */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px 14px',
                                background: isOn ? 'rgba(40, 167, 69, 0.15)' : 'rgba(255,255,255,0.06)',
                                border: `1px solid ${isOn ? 'rgba(40,167,69,0.4)' : 'rgba(255,255,255,0.12)'}`,
                                borderRadius: 10,
                                marginBottom: 14,
                                fontSize: 13,
                            }}>
                                <span>
                                    <strong style={{ fontWeight: 700 }}>{isOn ? 'Availability ON' : 'Availability OFF'}</strong>
                                    {' · '}
                                    {windowText}
                                </span>
                                <span style={{ display: 'flex', gap: 12, fontSize: 11, opacity: 0.8 }}>
                                    <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'rgba(40,167,69,0.6)', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Available</span>
                                    <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'rgba(220,53,69,0.6)', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Unavailable</span>
                                </span>
                            </div>

                            {this.state.loading ? (
                                <div style={{ textAlign: 'center', padding: 30, opacity: 0.7 }}>Loading availability…</div>
                            ) : this.state.err ? (
                                <div style={{ textAlign: 'center', padding: 20, color: '#ff8a8a' }}>{this.state.err}</div>
                            ) : (
                                <>
                                    {this.renderHeaderBar()}
                                    {this.renderCalendarGrid()}
                                    {this.renderSlotDetails()}
                                </>
                            )}
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        );
    }
}

export default PhotographerAvailabilityModal;
